import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type {
  AdminTrackingPixels,
  AdminUpdateTrackingPixelsRequest,
  TrackingPixels,
} from "@ternary/contracts"
import { Button, ToneIcon } from "@ternary/ui"
import { type FormEvent, useId, useRef, useState } from "react"
import { flushSync } from "react-dom"
import { loadAdminTrackingPixels, updateAdminTrackingPixels } from "../api.js"
import { FormLoading } from "../components/form-loading.js"
import { errorDetail, LoadError } from "../components/load-error.js"
import {
  PIXEL_PROVIDERS,
  type PixelErrors,
  PixelFields,
  trimPixels,
  validatePixels,
} from "../components/pixel-fields.js"
import { ViewOnlyNotice } from "../components/view-only.js"
import { ConnectionsHeader } from "./admin-discord-page.js"

/**
 * The pixels form. `savedAt` is when the saved pixels were last read or saved: each time, an ID
 * left alone since takes the saved value (trimmed, after a Save), and one edited meanwhile, even
 * while a Save was in flight, keeps the edit. A remount would drop that edit. A Save that fails
 * puts the base back, so the next read does not take its unsaved IDs back to the saved ones. While
 * a Save is in flight, a read that does not hold what it sent merges against the base from before
 * the Save, so the IDs being saved stay the draft's whatever the Save then answers.
 */
function TrackingPixelsForm({
  catalog,
  id,
  onSave,
  savedAt,
}: {
  catalog: AdminTrackingPixels
  id: string
  onSave(pixels: AdminUpdateTrackingPixelsRequest, onSettled: (saved: boolean) => void): void
  savedAt: number
}) {
  const [draft, setDraft] = useState(catalog.pixels)
  // What each ID in the draft was last taken from: the saved pixels, or the draft a Save sent.
  const [base, setBase] = useState(catalog.pixels)
  const [readAt, setReadAt] = useState(savedAt)
  // The Save in flight: the base it replaced, and the draft it sent.
  const [saving, setSaving] = useState<{ previous: TrackingPixels; sent: TrackingPixels } | null>(
    null,
  )
  if (savedAt !== readAt) {
    setReadAt(savedAt)
    // A read holding what the Save sent is its answer, so the sent IDs take their saved (trimmed)
    // values; any other read in the meantime is compared with the base from before the Save.
    const landed =
      saving !== null &&
      PIXEL_PROVIDERS.every(({ key }) => trimPixels(saving.sent)[key] === catalog.pixels[key])
    const against = saving === null ? base : landed ? saving.sent : saving.previous
    if (landed) setSaving(null)
    setBase(catalog.pixels)
    setDraft((current) => {
      const next = { ...current }
      for (const { key } of PIXEL_PROVIDERS) {
        if (current[key] === against[key]) next[key] = catalog.pixels[key]
      }
      return next
    })
  }
  const [errors, setErrors] = useState<PixelErrors>({})
  const formRef = useRef<HTMLFormElement>(null)

  // Editing an ID takes its error away until the next Save checks it again.
  function change(next: TrackingPixels) {
    const kept = { ...errors }
    for (const { key } of PIXEL_PROVIDERS) if (next[key] !== draft[key]) delete kept[key]
    setErrors(kept)
    setDraft(next)
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    const found = validatePixels(draft)
    // Rendered now, so the first invalid field can take focus.
    flushSync(() => setErrors(found))
    if (Object.keys(found).length > 0) {
      formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus()
      return
    }
    const previous = base
    const sent = draft
    setBase(sent)
    setSaving({ previous, sent })
    onSave(trimPixels(sent), (saved) => {
      setSaving(null)
      // Unless a read since has set the base already.
      if (!saved) setBase((current) => (current === sent ? previous : current))
    })
  }

  return (
    <form
      aria-label="Tracking pixels"
      className="admin-stack"
      id={id}
      noValidate
      onSubmit={submit}
      ref={formRef}
    >
      <PixelFields
        disabled={!catalog.writable}
        errors={errors}
        onChange={change}
        saved={catalog.pixels}
        value={draft}
      />
      <p className="admin-note">
        Only after a visitor accepts cookies, we send <strong>PageView</strong> on every signup
        page, <strong>Lead</strong> on their details and <strong>CompleteRegistration</strong> when
        they join.
      </p>
    </form>
  )
}

export function AdminPixelsPage() {
  const formId = useId()
  const queryClient = useQueryClient()
  const query = useQuery({ queryFn: loadAdminTrackingPixels, queryKey: ["admin-pixels"] })
  const [notice, setNotice] = useState("")
  const [actionError, setActionError] = useState("")
  const update = useMutation({
    mutationFn: updateAdminTrackingPixels,
    onMutate() {
      setNotice("")
      setActionError("")
    },
    onSuccess(data) {
      queryClient.setQueryData(["admin-pixels"], data)
      // Tracking is on the dashboard's setup checklist (G7).
      void queryClient.invalidateQueries({ queryKey: ["admin-setup"] })
      setNotice("Tracking pixels saved.")
    },
    onError(error) {
      setActionError(error instanceof Error ? error.message : "Tracking pixels could not be saved.")
    },
  })

  if (query.isPending || query.isError) {
    return (
      <section aria-busy={query.isPending || undefined} className="admin-stack admin-page">
        <ConnectionsHeader active="pixels" />
        {query.isPending ? (
          <FormLoading fields={3} label="Loading tracking pixels…" />
        ) : (
          <LoadError
            detail={errorDetail(query.error)}
            message="We couldn't load your tracking pixels"
            onRetry={() => void query.refetch()}
            retrying={query.isFetching}
          />
        )}
      </section>
    )
  }

  return (
    <section className="admin-stack admin-page">
      <ConnectionsHeader active="pixels" />
      {query.data.writable ? null : <ViewOnlyNotice />}
      <TrackingPixelsForm
        catalog={query.data}
        id={formId}
        onSave={(pixels, onSettled) =>
          update.mutate(pixels, {
            onError: () => onSettled(false),
            onSuccess: () => onSettled(true),
          })
        }
        savedAt={query.dataUpdatedAt}
      />
      {/* A view-only account has nothing to save. */}
      {query.data.writable ? (
        <div className="admin-save-bar">
          {actionError ? (
            <p className="admin-save-message tny-error" role="alert">
              {actionError}
            </p>
          ) : null}
          <p
            className={notice ? "admin-save-message is-success" : "admin-save-message"}
            role="status"
          >
            {notice ? (
              <>
                <ToneIcon tone="success" />
                {notice}
              </>
            ) : null}
          </p>
          <Button form={formId} loading={update.isPending} type="submit">
            Save
          </Button>
        </div>
      ) : null}
    </section>
  )
}
